/**
 * SqlValueInput — a one-line SQL value input with a suggestion list (see
 * `sqlValueSuggestions.ts`): the list opens as the user types a word and
 * filters on it; ↑ / ↓ move, Enter or Tab take the highlighted suggestion
 * (a function lands with the caret inside its parentheses), Esc closes the
 * list. With the list closed, Enter submits and Esc cancels — the caller's
 * `onSubmit` / `onCancel`. Ctrl+Space or ↓ opens the full list (and
 * `openWhenEmpty` shows it on an empty input). Spell-check and autocorrect
 * stay off: the text is SQL.
 */

import { forwardRef, useEffect, useId, useLayoutEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { Braces, Columns3, CornerDownRight, SquareFunction } from 'lucide-react';

import { Popover, PopoverAnchor, PopoverContent } from '../../../../components/ui/popover';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { cn } from '../../../../lib/utils';
import {
  matchSqlValueSuggestions,
  valueTokenAt,
  type SqlValueSuggestion,
  type SqlValueSuggestionKind,
} from '../utils/sqlValueSuggestions';

const KIND_ICON: Record<SqlValueSuggestionKind, typeof Braces> = {
  value: CornerDownRight,
  keyword: Braces,
  column: Columns3,
  function: SquareFunction,
};

export interface SqlValueInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onSubmit' | 'onKeyDown'> {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly suggestions: readonly SqlValueSuggestion[];
  /** Enter with no suggestion highlighted. */
  readonly onSubmit?: () => void;
  /** Esc with the list closed. */
  readonly onCancel?: () => void;
  /** Test-id root: the input gets it, the list `-suggestions`, rows `-suggestion-N`. */
  readonly testId: string;
  /** Open the full list while the input is focused and empty — a fresh value with nothing typed yet. */
  readonly openWhenEmpty?: boolean;
  /** Analytics event fired when a suggestion is taken, with `{ kind }`. */
  readonly trackEvent?: string;
}

export const SqlValueInput = forwardRef<HTMLInputElement, SqlValueInputProps>(function SqlValueInput(
  { value, onValueChange, suggestions, onSubmit, onCancel, testId, className, openWhenEmpty, trackEvent, ...inputProps },
  ref,
) {
  const { track } = useAnalytics();
  const listId = useId();
  const inputEl = useRef<HTMLInputElement | null>(null);
  const [caret, setCaret] = useState(value.length);
  // 'typed' filters on the word at the caret; 'all' (Ctrl+Space / ↓) lists everything for it.
  const [mode, setMode] = useState<'closed' | 'typed' | 'all'>(openWhenEmpty && !value ? 'all' : 'closed');
  const [active, setActive] = useState(0);
  const token = valueTokenAt(value, caret);
  const items =
    mode === 'closed' || !token || (mode === 'typed' && !token.text)
      ? []
      : matchSqlValueSuggestions(suggestions, token.text);
  // A lone suggestion that is exactly the word typed adds nothing — keep Enter for submit.
  const open = items.length > 0 && !(items.length === 1 && items[0].insertText.toLowerCase() === token?.text.toLowerCase());
  const highlighted = Math.min(active, items.length - 1);
  // Keyboard moves keep the highlighted row in view.
  useEffect(() => {
    if (open) document.getElementById(`${listId}-${highlighted}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, highlighted, listId]);

  const syncCaret = (): void => setCaret(inputEl.current?.selectionStart ?? value.length);
  // Where the caret goes once an accepted suggestion's value is rendered —
  // placed before the browser handles the next key, so typing right after
  // accepting `COALESCE()` lands inside the parentheses.
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (pendingCaret.current === null) return;
    inputEl.current?.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  }, [value]);

  const accept = (s: SqlValueSuggestion): void => {
    if (!token) return;
    // Selected text (an edit opens with its value selected) is replaced too.
    const end = Math.max(caret, inputEl.current?.selectionEnd ?? caret);
    const next = value.slice(0, token.start) + s.insertText + value.slice(end);
    const at = token.start + (s.caret ?? s.insertText.length);
    if (next === value) inputEl.current?.setSelectionRange(at, at);
    else pendingCaret.current = at;
    onValueChange(next);
    setMode('closed');
    if (trackEvent) track(trackEvent, { kind: s.kind });
    setCaret(at);
  };

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <input
          {...inputProps}
          ref={(el) => {
            inputEl.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) ref.current = el;
          }}
          type="text"
          value={value}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open ? `${listId}-${highlighted}` : undefined}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          data-testid={testId}
          className={cn(
            'rounded border border-input bg-background px-1.5 font-mono text-xs outline-none placeholder:text-muted-foreground/70 focus-visible:ring-1 focus-visible:ring-ring',
            className,
          )}
          onChange={(e) => {
            onValueChange(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
            setMode(openWhenEmpty && !e.target.value ? 'all' : 'typed');
            setActive(0);
          }}
          onSelect={syncCaret}
          // No early return on defaultPrevented: a host popover's Escape claim
          // (escapeClaimTarget) prevents the native event before this runs.
          onKeyDown={(e) => {
            if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              const step = e.key === 'ArrowDown' ? 1 : -1;
              setActive((highlighted + step + items.length) % items.length);
            } else if (open && e.key === 'Enter' && items[highlighted].insertText.toLowerCase() === token?.text.toLowerCase()) {
              // The highlighted row is what is typed already: Enter submits.
              e.preventDefault();
              setMode('closed');
              onSubmit?.();
            } else if (open && (e.key === 'Enter' || e.key === 'Tab')) {
              e.preventDefault();
              accept(items[highlighted]);
            } else if (open && e.key === 'Escape') {
              e.preventDefault();
              setMode('closed');
            } else if (e.key === 'ArrowDown' || (e.key === ' ' && (e.ctrlKey || e.metaKey))) {
              e.preventDefault();
              syncCaret();
              setMode('all');
              setActive(0);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              onSubmit?.();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancel?.();
            }
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-auto min-w-56 max-w-96 p-1"
        data-testid={`${testId}-suggestions`}
        // The input keeps focus and owns the keys; Esc is handled there too.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // A press on the scrollbar or padding must not take focus from the
        // input (a value pill commits on blur).
        onMouseDown={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (e.target === inputEl.current) e.preventDefault();
          else setMode('closed');
        }}
      >
        <ul id={listId} role="listbox" className="m-0 max-h-48 list-none overflow-y-auto p-0">
          {items.map((s, i) => {
            const Icon = KIND_ICON[s.kind];
            return (
              <li
                key={`${s.kind}:${s.label}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === highlighted}
                data-testid={`${testId}-suggestion-${i}`}
                className={cn(
                  'flex cursor-default items-center gap-2 rounded-sm px-2 py-1 text-xs',
                  i === highlighted && 'bg-accent text-accent-foreground',
                )}
                // mousedown, not click: the input must not blur (a value pill commits on blur).
                onMouseDown={(e) => {
                  e.preventDefault();
                  accept(s);
                }}
                onMouseMove={() => setActive(i)}
              >
                <Icon aria-hidden className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate font-mono">{s.label}</span>
                {s.detail ? <span className="shrink-0 text-[10px] text-muted-foreground">{s.detail}</span> : null}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
});
